"use client";

import { ChevronDown, Minus, Plus } from "lucide-react";
import { memo, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  FlightFormHelper,
  PassengerHelper,
} from "@/feature/flight/search/lib/form";
import { PASSENGER_TYPES_LIST as PASSENGER_TYPES } from "@/feature/flight/search/types/constants";
import {
  TFlightSearchFormData,
  type TPassengerCounts,
} from "@/feature/flight/search/types/search";
import { cn } from "@/lib/utils";
import { UseFormReturn } from "react-hook-form";

interface BaseProps {
  passengers: TPassengerCounts;
  onChange: (passengers: TPassengerCounts) => void;
  isError?: boolean;
  totalOnly?: boolean;
  form: UseFormReturn<TFlightSearchFormData>;
}
interface WithChildAges extends BaseProps {
  withChildAges: true;
  children: React.ReactNode;
}
interface NoChildAges extends BaseProps {
  withChildAges?: false | undefined;
  children?: never;
}

export type PassengerProps = WithChildAges | NoChildAges;

export default function PassengerSelect({
  passengers,
  onChange,
  isError,
  totalOnly = false,
  withChildAges = false,
  children,
  form,
}: PassengerProps) {
  const [open, setOpen] = useState(false);
  const passengerTypesToRender: Array<keyof TPassengerCounts> = totalOnly
    ? ["adults"]
    : [...PASSENGER_TYPES];
  const hasChildAgeFields =
    !totalOnly && withChildAges && passengers.children > 0;

  // Simple calculations - no need for useMemo
  const totalPassengers = FlightFormHelper.getTotalPassengers(passengers);
  const canAddInfant = FlightFormHelper.canAddInfant(
    passengers.infants,
    passengers.adults,
    9,
    totalPassengers,
  );
  const canAddPassenger = FlightFormHelper.canAddPassenger(totalPassengers);
  const passengerText = FlightFormHelper.formatPassengerText(totalPassengers);
  const compactPassengerText = `${totalPassengers} Pax`;

  // Handle passenger updates internally
  const handleUpdatePassenger = (
    type: keyof TPassengerCounts,
    increment: boolean,
  ) => {
    const currentValue = passengers[type];
    const newValue = PassengerHelper.updateCount(
      type,
      currentValue,
      increment,
      passengers,
    );

    if (newValue !== null) {
      onChange({
        ...passengers,
        [type]: newValue,
      });
    }
  };

  const validateBeforeClose = async () => {
    const fieldsToValidate = hasChildAgeFields
      ? (["passengers", "childrenAges"] as const)
      : (["passengers"] as const);
    const isValid = await form.trigger(fieldsToValidate);

    if (isValid) {
      setOpen(false);
      return;
    }

    setOpen(true);
  };

  const handleDoneClick = async () => {
    await validateBeforeClose();
  };

  return (
    <Popover
      open={open}
      onOpenChange={(nextOpen) => {
        if (nextOpen) {
          setOpen(true);
          return;
        }

        void validateBeforeClose();
      }}
      modal
    >
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          aria-invalid={isError}
          aria-label={`Passengers: ${passengerText}`}
          className={cn(
            "h-8! w-full min-w-0 justify-start rounded-lg border border-border/60 bg-background/85 px-2 text-xs font-semibold text-foreground shadow-xs hover:bg-accent/60 md:rounded-md md:border-0 md:bg-primary/10 md:px-4 md:text-sm md:text-primary md:shadow-none",
            isError &&
              "border-destructive bg-destructive/5 text-destructive hover:border-destructive hover:bg-destructive/10 hover:text-destructive md:border md:border-destructive md:bg-destructive/10 md:text-destructive",
          )}
        >
          <span className="truncate md:hidden">{compactPassengerText}</span>
          <span className="hidden truncate md:inline">{passengerText}</span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-100" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-70 max-h-87.5 overflow-y-auto pt-2 pb-0 px-2"
        align="start"
        onInteractOutside={(event) => {
          event.preventDefault();
          void validateBeforeClose();
        }}
      >
        <div className="space-y-4">
          {passengerTypesToRender.map((passengerType) => {
            const count = passengers[passengerType];
            const canDecrement =
              passengerType === "adults" ? count > 1 : count > 0;
            const canIncrement =
              passengerType === "infants" ? canAddInfant : canAddPassenger;

            return (
              <div key={passengerType} className="space-y-4">
                <PassengerCounter
                  passengerType={passengerType}
                  count={count}
                  canDecrement={canDecrement}
                  canIncrement={canIncrement}
                  onUpdate={handleUpdatePassenger}
                  title={totalOnly ? "Passengers" : undefined}
                  subtitle={totalOnly ? "All travelers" : undefined}
                />

                {passengerType === "children" &&
                  !totalOnly &&
                  passengers.children > 0 &&
                  withChildAges && <div>{children}</div>}
              </div>
            );
          })}

          <div className=" sticky bottom-0 w-full bg-background/95 pb-2">
            <Button
              disabled={isError}
              type="button"
              onClick={handleDoneClick}
              className="w-full"
            >
              Done
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

//! passenger counter component
interface PassengerCounterProps {
  passengerType: keyof TPassengerCounts;
  count: number;
  canDecrement: boolean;
  canIncrement: boolean;
  onUpdate: (type: keyof TPassengerCounts, increment: boolean) => void;
  title?: string;
  subtitle?: string;
}
const PassengerCounter = memo(function PassengerCounter({
  passengerType,
  count,
  canDecrement,
  canIncrement,
  onUpdate,
  title,
  subtitle,
}: PassengerCounterProps) {
  const description = PassengerHelper.getPassengerDescription(passengerType);

  return (
    <div className="flex items-center justify-between">
      <div>
        <div className="font-medium">{title ?? description.title}</div>
        <div className="text-sm text-muted-foreground">
          {subtitle ?? description.subtitle}
        </div>
      </div>
      <div className="flex items-center gap-3">
        <Button
          type="button"
          variant="outline"
          size="icon-xs"
          onClick={() => onUpdate(passengerType, false)}
          disabled={!canDecrement}
        >
          <Minus className="h-4 w-4" />
        </Button>
        <span className="w-8 text-center">{count}</span>
        <Button
          type="button"
          variant="outline"
          size="icon-xs"
          onClick={() => onUpdate(passengerType, true)}
          disabled={!canIncrement}
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
});
